import { useState, useCallback, useMemo } from 'react';
import { MoodType, moodActivities, MoodActivity } from '@/data/moodActivities';

interface DailyMoodState {
  mood: MoodType | null;
  activityId: string | null;
  dismissed: boolean;
}

const getTodayKey = (userId: string): string => {
  const today = new Date().toISOString().split('T')[0];
  return `ifindlife-daily-mood-${userId}-${today}`;
};

const getStoredState = (userId: string): DailyMoodState | null => {
  try {
    const stored = localStorage.getItem(getTodayKey(userId));
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const saveState = (userId: string, state: DailyMoodState): void => {
  try {
    localStorage.setItem(getTodayKey(userId), JSON.stringify(state));
  } catch {
    // localStorage might be full or unavailable
  }
};

// Deterministic daily random: pick activity index based on date + mood
const getDailyActivityIndex = (mood: MoodType): number => {
  const today = new Date().toISOString().split('T')[0];
  let hash = 0;
  const seed = today + mood;
  for (let i = 0; i < seed.length; i++) {
    hash = ((hash << 5) - hash) + seed.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash) % 3;
};

export const useDailyMood = (userId: string | undefined) => {
  const [state, setState] = useState<DailyMoodState>(() => {
    if (!userId) return { mood: null, activityId: null, dismissed: false };
    return getStoredState(userId) || { mood: null, activityId: null, dismissed: false };
  });

  const selectMood = useCallback((mood: MoodType) => {
    if (!userId) return;
    const index = getDailyActivityIndex(mood);
    const activity = moodActivities[mood][index];
    const newState: DailyMoodState = { mood, activityId: activity.id, dismissed: false };
    setState(newState);
    saveState(userId, newState);
  }, [userId]);

  const dismiss = useCallback(() => {
    if (!userId) return;
    const newState: DailyMoodState = { ...state, dismissed: true };
    setState(newState);
    saveState(userId, newState);
  }, [userId, state]);

  const resetMood = useCallback(() => {
    if (!userId) return;
    const newState: DailyMoodState = { mood: null, activityId: null, dismissed: false };
    setState(newState);
    saveState(userId, newState);
  }, [userId]);

  const currentActivity: MoodActivity | null = useMemo(() => {
    if (!state.mood || !state.activityId) return null;
    const activities = moodActivities[state.mood];
    return activities.find(a => a.id === state.activityId) || null;
  }, [state.mood, state.activityId]);

  return {
    mood: state.mood,
    activity: currentActivity,
    dismissed: state.dismissed,
    selectMood,
    dismiss,
    resetMood,
  };
};
